import React, { type FunctionComponent, useMemo } from 'react'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import { spacing, NewSelect } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { SearchIcon } from '@instacart/ids-core'
import {
  PropertyTitle,
  SectionHeader,
  FullWidthProperty,
} from '../../shared-styled-components/SharedStyledComponents'
import { useDomainMessages } from '../../../utils/domain/intl'
import { useListBridgesQuery } from '../../../api'
import { type FormProps } from './StepAttributeForm'

const StyledPropertyTitle = styled(PropertyTitle)({
  minWidth: '200px',
})

const DataBridgeContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  flex: 1,
})

export const DataBridgeForm: FunctionComponent<FormProps> = ({
  attributes,
  mode,
  onChange,
  version,
}) => {
  const i18n = useDomainMessages({
    DataBridgeDetails: 'catalogAdminDomain.attributeManagement.form.dataBridge.details',
    DataBridgeName: 'catalogAdminDomain.attributeManagement.form.dataBridge.name',
    DataBridgeNamePlaceholder:
      'catalogAdminDomain.attributeManagement.form.dataBridge.name.placeholder',
  })

  const { data: apiListBridges, loading: listBridgesLoading } = useListBridgesQuery({
    variables: {
      limit: 1000,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
  })

  const bridgeOptions = useMemo(
    () =>
      apiListBridges?.instacart_catalog_data_bridge_v1_DataBridgeService_ListBridges?.bridges
        ?.map(bridge => ({
          label: bridge.name ?? '',
          value: bridge.name ?? '',
        }))
        .sort((a, b) => (a.value || '').localeCompare(b.value || '')) ?? [],
    [apiListBridges?.instacart_catalog_data_bridge_v1_DataBridgeService_ListBridges?.bridges]
  )

  return (
    <>
      <SectionHeader>{i18n.DataBridgeDetails}</SectionHeader>
      <FullWidthProperty
        style={{
          gap: '12px',
          justifyContent: 'flex-start',
          paddingTop: spacing.X8,
          paddingBottom: spacing.X8,
        }}
      >
        <StyledPropertyTitle>{i18n.DataBridgeName}</StyledPropertyTitle>
        {mode !== 'edit' ? (
          <div>{attributes.bridgeName}</div>
        ) : (
          <DataBridgeContainer>
            <NewSelect
              icon={SearchIcon}
              placeholder={i18n.DataBridgeNamePlaceholder}
              onChange={value => {
                if (mode === 'edit') {
                  onChange({ ...attributes, bridgeName: value })
                }
              }}
              options={bridgeOptions}
              value={attributes.bridgeName}
              isLoading={listBridgesLoading}
              isSearchable={true}
              styles={{
                container: provided => ({
                  ...provided,
                  flex: 1,
                  minWidth: '300px',
                }),
              }}
            />
          </DataBridgeContainer>
        )}
      </FullWidthProperty>
      <Divider />
    </>
  )
}
